package com.shadowings.gero.data.lessons.chapter13

import com.shadowings.gero.data.lessons.frenchClause
import com.shadowings.gero.data.lessons.germanObliqueSingular
import com.shadowings.gero.data.lessons.moveGermanSeparableParticleToEnd
import com.shadowings.gero.data.lessons.chapter11.FALLBACK_IO_WORD
import com.shadowings.gero.data.lessons.chapter11.buildWordBankFromWords
import com.shadowings.gero.data.lessons.chapter11.getEnTrainDeFr
import com.shadowings.gero.data.lessons.chapter11.getGerundEn
import com.shadowings.gero.data.lessons.chapter11.getGerundEs
import com.shadowings.gero.data.lessons.chapter11.getGerundIt
import com.shadowings.gero.data.lessons.chapter11.pickPronounOrFallback
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbDe
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEn
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEs
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbFr
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbIt
import com.shadowings.gero.data.lessons.isItForcePresent
import com.shadowings.gero.data.lessons.formatEnglishInPlace
import com.shadowings.gero.data.lessons.formatEnglishObjectPhrase
import com.shadowings.gero.data.lessons.formatEnglishSubject
import com.shadowings.gero.data.lessons.formatFrenchInPlace
import com.shadowings.gero.data.lessons.formatFrenchSubject
import com.shadowings.gero.data.lessons.formatGermanInPlace
import com.shadowings.gero.data.lessons.formatGermanObject
import com.shadowings.gero.data.lessons.formatGermanSubject
import com.shadowings.gero.data.lessons.formatItalianInPlace
import com.shadowings.gero.data.lessons.formatItalianSubject
import com.shadowings.gero.data.lessons.formatSpanishInPlace
import com.shadowings.gero.data.lessons.formatSpanishObject
import com.shadowings.gero.data.lessons.animalObjectIsThing
import com.shadowings.gero.data.lessons.isHumanSubject
import com.shadowings.gero.data.lessons.isCommonLivingNoun
import com.shadowings.gero.data.lessons.formatSpanishSubject
import com.shadowings.gero.data.lessons.getCleanMeaning
import com.shadowings.gero.data.lessons.hasVerbKey
import com.shadowings.gero.data.lessons.isStativeVerb
import com.shadowings.gero.data.lessons.isGermanDirectionalVerb
import com.shadowings.gero.data.lessons.pickDistinctIdsByBaseWord
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import kotlin.random.Random

// The lesson 2 (preposizione_hr) grammar point: a pseudoverbal continuous-action
// sentence (subject/pronoun + iw + ḥr + infinitive, chapter 9/chapter13 lesson 1)
// can carry a second, genuinely prepositional ḥr at the end introducing a place
// ("on top of") or distribution ("for/to each") complement - the "altra forma"
// D2h2:D21 shown in the lesson, alongside the everyday D2h2:Z1 spelling. Real
// Middle Egyptian disambiguates the two ḥr's from context; we render that
// ambiguity literally (both as "ḥr" in transliteration) and vary the second
// one's hieroglyphs randomly between the two spellings, same idea as
// PseudoverbalSentenceUtils.applyHieroglyphVariations's orthographic variants.
enum class HrComplementType { PLACE, DISTRIBUTION }

data class PseudoverbalHrComplementSentence(
    val hieroglyphs: String,
    val transliteration: String,
    val translationIt: String,
    val translationEn: String,
    val translationDe: String,
    val translationEs: String,
    val translationFr: String,
    val isPronominal: Boolean,
    val subjectKey: String,
    val verbKey: String,
    val objectKeys: List<String>,
    val complementKey: String,
    val complementType: HrComplementType,
    val isNegative: Boolean = false
) {
    fun getTranslation(languageCode: String): String = when (languageCode) {
        "it" -> translationIt
        "de" -> translationDe
        "es" -> translationEs
        "fr" -> translationFr
        else -> translationEn
    }
}

private fun complementPoolOf(row: SentenceOptions, type: HrComplementType): List<String> =
    if (type == HrComplementType.PLACE) row.hrPlaceComplements else row.hrDistributionComplements

fun hasHrComplementCandidate(
    type: HrComplementType,
    verbs: List<Verb>,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): Boolean = verbSentences.any { row ->
    row.subjects.isNotEmpty() &&
        complementPoolOf(row, type).isNotEmpty() &&
        verbs.hasVerbKey(row.verbs.first())
}

// m-locativo's own complement pool: sentences_verbial.tsv's dedicated
// complemento_luogo_m column - a fully disjoint word list from
// hrPlaceComplements (ḥr's own "su/on top of" column). The TSV column a word
// is listed under is the only source of truth for which preposition it takes;
// there's no per-word override to maintain in words.tsv.
fun hasMLocativeCandidate(
    verbs: List<Verb>,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): Boolean = verbSentences.any { row ->
    row.subjects.isNotEmpty() &&
        row.mPlaceComplements.isNotEmpty() &&
        verbs.hasVerbKey(row.verbs.first())
}

// "su/sul/sulla..." — ḥr's locative sense, mirrors formatXInPlace in
// AdverbialSentenceUtils.kt (built for the unrelated `m` preposition, and
// reused directly by formatMComplement below) with "su"/"on"/"auf"/"sobre"/
// "sur" instead of "in"/"en"/"dans". Every word in hrPlaceComplements is
// surface-type by construction of the TSV column, so this always applies the
// "su" default - no per-word exception to check.
private fun formatItalianOnPlace(place: Word): String {
    val art = place.getArticolo("it")
    val meaning = getCleanMeaning(place, "it")
    if (art.isNullOrBlank() || art == "-") return "su $meaning"
    val contracted = when (art) {
        "il" -> "sul"
        "lo" -> "sullo"
        "l'" -> "sull'"
        "la" -> "sulla"
        "i" -> "sui"
        "gli" -> "sugli"
        "le" -> "sulle"
        else -> "su $art"
    }
    return if (contracted.endsWith("'")) "$contracted$meaning" else "$contracted $meaning"
}

private fun formatEnglishOnPlace(place: Word): String {
    val art = place.getArticolo("en")
    val meaning = getCleanMeaning(place, "en")
    return if (art.isNullOrBlank() || art == "-") "on $meaning" else "on $art $meaning"
}

// `accusative`: the verb puts something onto the place (words.tsv
// de_directional, "bringt das Gold auf den Altar"); otherwise the dative of
// position ("isst den Fisch auf dem Altar").
private fun formatGermanOnPlace(place: Word, accusative: Boolean = false): String {
    val art = place.getArticolo("de")
    val meaning = getCleanMeaning(place, "de")
    if (art.isNullOrBlank() || art == "-") return "auf $meaning"
    if (accusative) {
        if (place.isPlural) return "auf die $meaning"
        return if (art == "der") "auf den ${germanObliqueSingular(place, meaning)}" else "auf $art $meaning"
    }
    if (place.isPlural) {
        val pluralNoun = if (meaning.endsWith("n") || meaning.endsWith("s")) meaning else "${meaning}n"
        return "auf den $pluralNoun"
    }
    return when (art) {
        "der", "das" -> "auf dem ${germanObliqueSingular(place, meaning)}"
        "die" -> "auf der $meaning"
        else -> "auf $art $meaning"
    }
}

private fun formatSpanishOnPlace(place: Word): String {
    val art = place.getArticolo("es")
    val meaning = getCleanMeaning(place, "es")
    return if (art.isNullOrBlank() || art == "-") "sobre $meaning" else "sobre $art $meaning"
}

private fun formatFrenchOnPlace(place: Word): String {
    val art = place.getArticolo("fr")
    val meaning = getCleanMeaning(place, "fr")
    if (art.isNullOrBlank() || art == "-") return "sur $meaning"
    return if (art.endsWith("'")) "sur $art$meaning" else "sur $art $meaning"
}

// m-locativo's complement translation: always "in"/"en"/"dans", reusing the
// exact formatters AdverbialSentenceUtils.kt already built for chapter 9's
// simpler "m" lesson (made internal so this file can call them directly)
// instead of duplicating the same contraction logic.
internal fun formatMComplement(word: Word, languageCode: String, deAccusative: Boolean = false): String = when (languageCode) {
    "it" -> formatItalianInPlace(word)
    "de" -> formatGermanInPlace(word, deAccusative)
    "es" -> formatSpanishInPlace(word)
    "fr" -> formatFrenchInPlace(word)
    else -> formatEnglishInPlace(word)
}

// "per ogni/for each/für jeden/para cada/pour chaque X" — ḥr's distributive
// sense. The two verbs that ever carry a hrDistributionComplements pool
// (OFFRIRE/DARE) both take a recipient, so the natural preposition here is
// "to each X" ("offering bread TO each priest"), not "for each X" - "per
// ogni"/"for each"/etc. reads as "on behalf of", which is wrong once there's
// an explicit recipient-taking verb (contrast the lesson's own standalone
// caption "Pane per ogni sacerdote", a verbless label where "for each" is
// fine - that line is untouched, this formatter is only for the generated
// verb sentences). Unlike the place complement, the determiner replaces the
// article entirely (no "a ogni il sacerdote"), so no articolo/contraction
// handling — except German, where "jedem/jeder" is the recipient marked by
// the dative case alone, no preposition ("bietet das Gold jedem Priester",
// not "an jeden Priester" - "an" + accusative was wrong, caught in review).
private fun formatItalianToEach(word: Word): String = "a ogni ${getCleanMeaning(word, "it")}"
private fun formatEnglishToEach(word: Word): String = "to each ${getCleanMeaning(word, "en")}"
private fun formatSpanishToEach(word: Word): String = "a cada ${getCleanMeaning(word, "es")}"
private fun formatFrenchToEach(word: Word): String = "à chaque ${getCleanMeaning(word, "fr")}"
private fun formatGermanToEach(word: Word): String {
    val meaning = getCleanMeaning(word, "de")
    val jeder = when (word.getArticolo("de")) {
        "der", "das" -> "jedem"
        "die" -> "jeder"
        else -> "jedem"
    }
    return "$jeder ${germanObliqueSingular(word, meaning)}"
}

// `deAccusative` only affects the German place complement; the distributive
// "jedem/jeder" is the recipient and stays dative.
internal fun formatComplement(
    word: Word,
    type: HrComplementType,
    languageCode: String,
    deAccusative: Boolean = false
): String =
    when (type) {
        HrComplementType.PLACE -> when (languageCode) {
            "it" -> formatItalianOnPlace(word)
            "de" -> formatGermanOnPlace(word, deAccusative)
            "es" -> formatSpanishOnPlace(word)
            "fr" -> formatFrenchOnPlace(word)
            else -> formatEnglishOnPlace(word)
        }
        HrComplementType.DISTRIBUTION -> when (languageCode) {
            "it" -> formatItalianToEach(word)
            "de" -> formatGermanToEach(word)
            "es" -> formatSpanishToEach(word)
            "fr" -> formatFrenchToEach(word)
            else -> formatEnglishToEach(word)
        }
    }

// The 9-way person table every pseudoverbal builder in chapter11 repeats per
// sentence shape (subject/verb, +object, +complex object) - factored into one
// function here since this file only needs it for a single shape (subject +
// verb + optional object + ḥr-complement).
private fun pronominalContinuousPrefix(
    pronoun: Word,
    verbKey: String,
    languageCode: String,
    isStative: Boolean = false,
    itForcePresent: Boolean = false,
    hasObject: Boolean = false
): String {
    return when (languageCode) {
        "it" -> {
            if (itForcePresent) {
                val v = conjugateVerbIt(verbKey, pronoun.id)
                when (pronoun.id) {
                    "IO" -> "io $v"
                    "TU_M" -> "tu (m.) $v"
                    "TU_F" -> "tu (f.) $v"
                    "EGLI" -> "egli $v"
                    "ELLA" -> "ella $v"
                    "ESSO" -> "esso $v"
                    "NOI" -> "noi $v"
                    "VOI" -> "voi $v"
                    "ESSI" -> "essi $v"
                    else -> "egli $v"
                }
            } else {
                val g = getGerundIt(verbKey)
                when (pronoun.id) {
                    "IO" -> "io sto $g"
                    "TU_M" -> "tu (m.) stai $g"
                    "TU_F" -> "tu (f.) stai $g"
                    "EGLI" -> "egli sta $g"
                    "ELLA" -> "ella sta $g"
                    "ESSO" -> "esso sta $g"
                    "NOI" -> "noi stiamo $g"
                    "VOI" -> "voi state $g"
                    "ESSI" -> "essi stanno $g"
                    else -> "egli sta $g"
                }
            }
        }
        "de" -> {
            val v = conjugateVerbDe(verbKey, pronoun.id, hasObject)
            when (pronoun.id) {
                "IO" -> "ich $v"
                "TU_M" -> "du (m.) $v"
                "TU_F" -> "du (f.) $v"
                "EGLI" -> "er $v"
                "ELLA" -> "sie $v"
                "ESSO" -> "es $v"
                "NOI" -> "wir $v"
                "VOI" -> "ihr $v"
                "ESSI" -> "sie $v"
                else -> "er $v"
            }
        }
        "es" -> {
            if (isStative) {
                val v = conjugateVerbEs(verbKey, pronoun.id)
                when (pronoun.id) {
                    "IO" -> "yo $v"
                    "TU_M" -> "tú (m.) $v"
                    "TU_F" -> "tú (f.) $v"
                    "EGLI" -> "él $v"
                    "ELLA" -> "ella $v"
                    "ESSO" -> "eso $v"
                    "NOI" -> "nosotros $v"
                    "VOI" -> "vosotros $v"
                    "ESSI" -> "ellos $v"
                    else -> "él $v"
                }
            } else {
                val g = getGerundEs(verbKey)
                when (pronoun.id) {
                    "IO" -> "yo estoy $g"
                    "TU_M" -> "tú (m.) estás $g"
                    "TU_F" -> "tú (f.) estás $g"
                    "EGLI" -> "él está $g"
                    "ELLA" -> "ella está $g"
                    "ESSO" -> "eso está $g"
                    "NOI" -> "nosotros estamos $g"
                    "VOI" -> "vosotros estáis $g"
                    "ESSI" -> "ellos están $g"
                    else -> "él está $g"
                }
            }
        }
        "fr" -> {
            if (isStative) {
                val v = conjugateVerbFr(verbKey, pronoun.id)
                when (pronoun.id) {
                    "IO" -> frenchClause("je", v)
                    "TU_M" -> "tu (m.) $v"
                    "TU_F" -> "tu (f.) $v"
                    "EGLI" -> "il $v"
                    "ELLA" -> "elle $v"
                    "ESSO" -> "cela $v"
                    "NOI" -> "nous $v"
                    "VOI" -> "vous $v"
                    "ESSI" -> "ils $v"
                    else -> "il $v"
                }
            } else {
                val g = getEnTrainDeFr(verbKey)
                when (pronoun.id) {
                    "IO" -> "je suis $g"
                    "TU_M" -> "tu (m.) es $g"
                    "TU_F" -> "tu (f.) es $g"
                    "EGLI" -> "il est $g"
                    "ELLA" -> "elle est $g"
                    "ESSO" -> "cela est $g"
                    "NOI" -> "nous sommes $g"
                    "VOI" -> "vous êtes $g"
                    "ESSI" -> "ils sont $g"
                    else -> "il est $g"
                }
            }
        }
        else -> {
            if (isStative) {
                val v = conjugateVerbEn(verbKey, pronoun.id)
                when (pronoun.id) {
                    "IO" -> "I $v"
                    "TU_M" -> "you (m.) $v"
                    "TU_F" -> "you (f.) $v"
                    "EGLI" -> "he $v"
                    "ELLA" -> "she $v"
                    "ESSO" -> "it $v"
                    "NOI" -> "we $v"
                    "VOI" -> "you $v"
                    "ESSI" -> "they $v"
                    else -> "he $v"
                }
            } else {
                val g = getGerundEn(verbKey)
                when (pronoun.id) {
                    "IO" -> "I am $g"
                    "TU_M" -> "you (m.) are $g"
                    "TU_F" -> "you (f.) are $g"
                    "EGLI" -> "he is $g"
                    "ELLA" -> "she is $g"
                    "ESSO" -> "it is $g"
                    "NOI" -> "we are $g"
                    "VOI" -> "you are $g"
                    "ESSI" -> "they are $g"
                    else -> "he is $g"
                }
            }
        }
    }
}

private fun nominalContinuousPrefix(
    subject: Word,
    verbKey: String,
    languageCode: String,
    isStative: Boolean = false,
    itForcePresent: Boolean = false,
    hasObject: Boolean = false
): String =
    when (languageCode) {
        "it" -> {
            val verbStr = if (itForcePresent) {
                val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
                conjugateVerbIt(verbKey, pronounCode)
            } else {
                if (subject.isPlural) "stanno ${getGerundIt(verbKey)}" else "sta ${getGerundIt(verbKey)}"
            }
            "${formatItalianSubject(subject)} $verbStr"
        }
        "de" -> {
            val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
            "${formatGermanSubject(subject)} ${conjugateVerbDe(verbKey, pronounCode, hasObject)}"
        }
        "es" -> {
            val verbStr = if (isStative) {
                val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
                conjugateVerbEs(verbKey, pronounCode)
            } else {
                if (subject.isPlural) "están ${getGerundEs(verbKey)}" else "está ${getGerundEs(verbKey)}"
            }
            "${formatSpanishSubject(subject)} $verbStr"
        }
        "fr" -> {
            val verbStr = if (isStative) {
                val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
                conjugateVerbFr(verbKey, pronounCode)
            } else {
                if (subject.isPlural) "sont ${getEnTrainDeFr(verbKey)}" else "est ${getEnTrainDeFr(verbKey)}"
            }
            "${formatFrenchSubject(subject)} $verbStr"
        }
        else -> {
            val verbStr = if (isStative) {
                val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
                conjugateVerbEn(verbKey, pronounCode)
            } else {
                if (subject.isPlural) "are ${getGerundEn(verbKey)}" else "is ${getGerundEn(verbKey)}"
            }
            "${formatEnglishSubject(subject)} $verbStr"
        }
    }

private fun objectPhrase(objectWord: Word?, languageCode: String, verbKey: String = ""): String {
    if (objectWord == null) return ""
    return when (languageCode) {
        "it" -> formatItalianSubject(objectWord)
        "de" -> formatGermanObject(objectWord)
        "es" -> formatSpanishObject(objectWord, animalObjectIsThing(verbKey, DatabaseRepositoryImpl.words))
        "fr" -> formatFrenchSubject(objectWord)
        else -> formatEnglishObjectPhrase(verbKey, formatEnglishSubject(objectWord))
    }
}

fun getRandomHrComplementSentence(
    type: HrComplementType,
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean? = null,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): PseudoverbalHrComplementSentence {
    val candidates = verbSentences.filter { row ->
        row.subjects.isNotEmpty() &&
            complementPoolOf(row, type).isNotEmpty() &&
            verbs.hasVerbKey(row.verbs.first())
    }
    require(candidates.isNotEmpty()) { "Nessuna riga con complemento ḥr di tipo $type disponibile" }

    val pronominal = isPronominal ?: random.nextBoolean()
    val humanCandidates = candidates.filter { row -> row.subjects.any { isHumanSubject(it, words) } }
    val chosenRow = if (pronominal && humanCandidates.isNotEmpty()) {
        humanCandidates.random(random)
    } else {
        candidates.random(random)
    }

    val verb = verbs.first { it.chiaveWords == chosenRow.verbs.first() }
    val usedBaseIds = mutableSetOf<String>()

    val objectWord = pickDistinctIdsByBaseWord(chosenRow.objects.shuffled(random), 1, words, usedBaseIds)
        .firstOrNull()
        ?.let { id -> words.firstOrNull { it.id == id } }

    val complementPool = complementPoolOf(chosenRow, type)
    val complementId = pickDistinctIdsByBaseWord(complementPool.shuffled(random), 1, words, usedBaseIds)
        .firstOrNull() ?: complementPool.first()
    val complementWord = words.firstOrNull { it.id == complementId } ?: words.first()

    val infHiero = verb.formaInfinita.ifBlank { verb.formaBaseSenzaDet }
    val infTrans = verb.traslitterazioneInfinita.ifBlank { verb.traslitterazione }
    val hr2 = if (random.nextBoolean()) "D2h2:Z1" else "D2h2:D21"
    val objectHieroPart = objectWord?.let { "-${it.gardiner}" } ?: ""
    val objectTransPart = objectWord?.let { " ${it.reading}" } ?: ""

    val subjectWord: Word
    val hieroglyphs: String
    val transliteration: String

    if (pronominal) {
        val pronoun = pickPronounOrFallback(words, random).let { candidate ->
            val availableInRow = chosenRow.subjects
                .mapNotNull { id -> words.firstOrNull { it.id == id && it.isPronoun } }
            availableInRow.randomOrNull(random) ?: candidate
        }
        subjectWord = pronoun
        val pronTrans = if (pronoun.reading.startsWith(".")) pronoun.reading else ".${pronoun.reading}"
        hieroglyphs =
            "M17-G43-${pronoun.gardiner}-D2h2:Z1-$infHiero$objectHieroPart-$hr2-${complementWord.gardiner}"
        transliteration = "iw$pronTrans ḥr $infTrans$objectTransPart ḥr ${complementWord.reading}"
    } else {
        val availableNounsInRow = pickDistinctIdsByBaseWord(chosenRow.subjects.shuffled(random), 1, words, mutableSetOf())
            .mapNotNull { id -> words.firstOrNull { it.id == id && !it.isPronoun } }
        subjectWord = availableNounsInRow.firstOrNull()
            ?: words.firstOrNull { it.isCommonLivingNoun() }
            ?: FALLBACK_IO_WORD
        hieroglyphs =
            "M17-G43-${subjectWord.gardiner}-D2h2:Z1-$infHiero$objectHieroPart-$hr2-${complementWord.gardiner}"
        transliteration = "iw ${subjectWord.reading} ḥr $infTrans$objectTransPart ḥr ${complementWord.reading}"
    }

    fun translationFor(languageCode: String): String {
        val isStative = isStativeVerb(verb.chiaveWords, words)
        val itForcePresent = isItForcePresent(verb.chiaveWords, words)
        val prefix = if (pronominal) {
            pronominalContinuousPrefix(subjectWord, verb.chiaveWords, languageCode, isStative, itForcePresent, objectWord != null)
        } else {
            nominalContinuousPrefix(subjectWord, verb.chiaveWords, languageCode, isStative, itForcePresent, objectWord != null)
        }
        val objStr = objectPhrase(objectWord, languageCode, verb.chiaveWords)
        val complementStr = formatComplement(
            complementWord, type, languageCode, isGermanDirectionalVerb(verb.chiaveWords, words)
        )
        return listOf(prefix, objStr, complementStr).filter { it.isNotBlank() }.joinToString(" ")
            .let { if (languageCode == "de") moveGermanSeparableParticleToEnd(it) else it }
    }

    return PseudoverbalHrComplementSentence(
        hieroglyphs = hieroglyphs,
        transliteration = transliteration,
        translationIt = translationFor("it"),
        translationEn = translationFor("en"),
        translationDe = translationFor("de"),
        translationEs = translationFor("es"),
        translationFr = translationFor("fr"),
        isPronominal = pronominal,
        subjectKey = subjectWord.id,
        verbKey = verb.chiaveWords,
        objectKeys = objectWord?.let { listOf(it.id) } ?: emptyList(),
        complementKey = complementWord.id,
        complementType = type
    )
}

// Lesson 3 (preposizione_m_locativa): the same "in a place" pseudoverbal
// shape as getRandomHrComplementSentence(PLACE) above, but the
// true-preposition complement is introduced by "m" (G17, single spelling —
// unlike ḥr's two D2h2 variants) instead of "ḥr", and picked from
// sentences_verbial.tsv's own dedicated complemento_luogo_m column
// (SentenceOptions.mPlaceComplements) rather than hrPlaceComplements - a
// fully disjoint word list, so ḥr never touches a container word and m never
// touches a surface word; formatMComplement always applies the "in"
// translation, no per-word exception to check. Kept as its own function
// (rather than a parameterized branch of getRandomHrComplementSentence) so
// the hr function's random-call sequence - and the golden-value tests pinned
// to it - stays untouched.
fun getRandomMLocativeSentence(
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean? = null,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): PseudoverbalHrComplementSentence {
    val type = HrComplementType.PLACE
    val candidates = verbSentences.filter { row ->
        row.subjects.isNotEmpty() &&
            row.mPlaceComplements.isNotEmpty() &&
            verbs.hasVerbKey(row.verbs.first())
    }
    require(candidates.isNotEmpty()) { "Nessuna riga con complemento m disponibile" }

    val pronominal = isPronominal ?: random.nextBoolean()
    val humanCandidates = candidates.filter { row -> row.subjects.any { isHumanSubject(it, words) } }
    val chosenRow = if (pronominal && humanCandidates.isNotEmpty()) {
        humanCandidates.random(random)
    } else {
        candidates.random(random)
    }

    val verb = verbs.first { it.chiaveWords == chosenRow.verbs.first() }
    val usedBaseIds = mutableSetOf<String>()

    val objectWord = pickDistinctIdsByBaseWord(chosenRow.objects.shuffled(random), 1, words, usedBaseIds)
        .firstOrNull()
        ?.let { id -> words.firstOrNull { it.id == id } }

    val complementPool = chosenRow.mPlaceComplements
    val complementId = pickDistinctIdsByBaseWord(complementPool.shuffled(random), 1, words, usedBaseIds)
        .firstOrNull() ?: complementPool.first()
    val complementWord = words.firstOrNull { it.id == complementId } ?: words.first()

    val infHiero = verb.formaInfinita.ifBlank { verb.formaBaseSenzaDet }
    val infTrans = verb.traslitterazioneInfinita.ifBlank { verb.traslitterazione }
    val objectHieroPart = objectWord?.let { "-${it.gardiner}" } ?: ""
    val objectTransPart = objectWord?.let { " ${it.reading}" } ?: ""

    val subjectWord: Word
    val hieroglyphs: String
    val transliteration: String

    if (pronominal) {
        val pronoun = pickPronounOrFallback(words, random).let { candidate ->
            val availableInRow = chosenRow.subjects
                .mapNotNull { id -> words.firstOrNull { it.id == id && it.isPronoun } }
            availableInRow.randomOrNull(random) ?: candidate
        }
        subjectWord = pronoun
        val pronTrans = if (pronoun.reading.startsWith(".")) pronoun.reading else ".${pronoun.reading}"
        hieroglyphs =
            "M17-G43-${pronoun.gardiner}-D2h2:Z1-$infHiero$objectHieroPart-G17-${complementWord.gardiner}"
        transliteration = "iw$pronTrans ḥr $infTrans$objectTransPart m ${complementWord.reading}"
    } else {
        val availableNounsInRow = pickDistinctIdsByBaseWord(chosenRow.subjects.shuffled(random), 1, words, mutableSetOf())
            .mapNotNull { id -> words.firstOrNull { it.id == id && !it.isPronoun } }
        subjectWord = availableNounsInRow.firstOrNull()
            ?: words.firstOrNull { it.isCommonLivingNoun() }
            ?: FALLBACK_IO_WORD
        hieroglyphs =
            "M17-G43-${subjectWord.gardiner}-D2h2:Z1-$infHiero$objectHieroPart-G17-${complementWord.gardiner}"
        transliteration = "iw ${subjectWord.reading} ḥr $infTrans$objectTransPart m ${complementWord.reading}"
    }

    fun translationFor(languageCode: String): String {
        val isStative = isStativeVerb(verb.chiaveWords, words)
        val itForcePresent = isItForcePresent(verb.chiaveWords, words)
        val prefix = if (pronominal) {
            pronominalContinuousPrefix(subjectWord, verb.chiaveWords, languageCode, isStative, itForcePresent, objectWord != null)
        } else {
            nominalContinuousPrefix(subjectWord, verb.chiaveWords, languageCode, isStative, itForcePresent, objectWord != null)
        }
        val objStr = objectPhrase(objectWord, languageCode, verb.chiaveWords)
        val complementStr = formatMComplement(
            complementWord, languageCode, isGermanDirectionalVerb(verb.chiaveWords, words)
        )
        return listOf(prefix, objStr, complementStr).filter { it.isNotBlank() }.joinToString(" ")
            .let { if (languageCode == "de") moveGermanSeparableParticleToEnd(it) else it }
    }

    return PseudoverbalHrComplementSentence(
        hieroglyphs = hieroglyphs,
        transliteration = transliteration,
        translationIt = translationFor("it"),
        translationEn = translationFor("en"),
        translationDe = translationFor("de"),
        translationEs = translationFor("es"),
        translationFr = translationFor("fr"),
        isPronominal = pronominal,
        subjectKey = subjectWord.id,
        verbKey = verb.chiaveWords,
        objectKeys = objectWord?.let { listOf(it.id) } ?: emptyList(),
        complementKey = complementWord.id,
        complementType = type
    )
}

fun buildMLocativeWordSelectionQuizzes(
    count: Int,
    random: Random,
    languageCode: String,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs
): List<IQuizContent> {
    if (!hasMLocativeCandidate(verbs, verbSentences)) return emptyList()
    val quizzes = mutableListOf<IQuizContent>()
    for (i in 0 until count) {
        var attempts = 0
        var quiz: WordSelectionContent? = null
        while (attempts < 5) {
            val sentence = getRandomMLocativeSentence(
                verbs = verbs,
                words = words,
                random = random,
                verbSentences = verbSentences
            )
            val correctPhrase = sentence.getTranslation(languageCode)
            val isDuplicate = quizzes.any { (it as? WordSelectionContent)?.correctPhrase == correctPhrase }
            val wordBank = buildWordBankFromWords(correctPhrase, words, verbs, languageCode, random)

            quiz = WordSelectionContent(
                hieroglyphs = sentence.hieroglyphs,
                correctPhrase = correctPhrase,
                words = wordBank,
                transliteration = null
            )
            if (!isDuplicate) break
            attempts++
        }
        quiz?.let { quizzes.add(it) }
    }
    return quizzes
}

// Builds WordSelectionContent-only quizzes (no transliteration shown, no
// TextTranslationContent) for the ḥr-complement sentence shape above. `type`
// is the requested complement kind; callers needing a guaranteed count should
// check hasHrComplementCandidate first and fall back to PLACE themselves —
// this function returns fewer than `count` rather than crash if the seen-
// filtered pool runs out mid-way.
fun buildHrComplementWordSelectionQuizzes(
    type: HrComplementType,
    count: Int,
    random: Random,
    languageCode: String,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs
): List<IQuizContent> {
    if (!hasHrComplementCandidate(type, verbs, verbSentences)) return emptyList()
    val quizzes = mutableListOf<IQuizContent>()
    for (i in 0 until count) {
        var attempts = 0
        var quiz: WordSelectionContent? = null
        while (attempts < 5) {
            val sentence = getRandomHrComplementSentence(
                type = type,
                verbs = verbs,
                words = words,
                random = random,
                verbSentences = verbSentences
            )
            val correctPhrase = sentence.getTranslation(languageCode)
            val isDuplicate = quizzes.any { (it as? WordSelectionContent)?.correctPhrase == correctPhrase }
            val wordBank = buildWordBankFromWords(correctPhrase, words, verbs, languageCode, random)

            quiz = WordSelectionContent(
                hieroglyphs = sentence.hieroglyphs,
                correctPhrase = correctPhrase,
                words = wordBank,
                transliteration = null
            )
            if (!isDuplicate) break
            attempts++
        }
        quiz?.let { quizzes.add(it) }
    }
    return quizzes
}
